import {
  AI_CHAT_STREAM_PATH,
  AiChatContext,
  AiChatRequest,
  AnswerLanguage,
  ChatTurn,
  ExchangeData,
  GlossaryInput,
  GlossaryOutput,
  LabelKeysData,
  MAX_CONVERSATION_CHARACTERS,
  MAX_TURNS,
  SourcesData
} from '@glow/ai-contract'
import { ChatTransport, DefaultChatTransport, UIMessage } from 'ai'
import { stripBase } from '../../utils/appBase'

// The exchange and sources data parts ride on the assistant message; ping keep-alives are transient
// and never stored. The tools entry types the 'tool-glossary' parts streamed while looking up terms.
export type AiChatUIMessage = UIMessage<
  unknown,
  { exchange: ExchangeData; sources: SourcesData; labelKeys: LabelKeysData; ping: unknown },
  { glossary: { input: GlossaryInput; output: GlossaryOutput } }
>

export const messageText = (message: AiChatUIMessage): string =>
  message.parts.flatMap((part) => (part.type === 'text' ? [part.text] : [])).join('')

// The oldest turns are dropped once the backend's limits would reject the request,
// so a long-lived session loses distant context instead of permanently failing
export const toTurns = (messages: AiChatUIMessage[]): ChatTurn[] => {
  const turns = messages.flatMap((message) => {
    const content = messageText(message)
    return (message.role === 'user' || message.role === 'assistant') && content.trim().length > 0
      ? [{ role: message.role, content }]
      : []
  })
  const characters = (trimmed: ChatTurn[]) => trimmed.reduce((sum, turn) => sum + turn.content.length, 0)
  while (turns.length > 1 && (turns.length > MAX_TURNS || characters(turns) > MAX_CONVERSATION_CHARACTERS)) {
    turns.shift()
  }
  return turns
}

export const toChatRequestBody = (
  messages: AiChatUIMessage[],
  conversationId: string | undefined,
  context?: AiChatContext,
  answerLanguage?: AnswerLanguage
): AiChatRequest => ({
  messages: toTurns(messages),
  // Resolved per request, so navigating between pages or picking another language keeps the context current
  context: { ...(context ?? { pathname: stripBase(globalThis.location.pathname) }), answerLanguage },
  conversationId
})

/**
 * Streams the AI SDK UI message stream protocol. The caller passes conversationId in the request
 * body of sendMessage and regenerate, so every request echoes the id captured from the previous
 * answer's exchange data part. No model is named, so the server answers with its default.
 */
export const createAiChatTransport = (context?: AiChatContext): ChatTransport<AiChatUIMessage> =>
  new DefaultChatTransport({
    api: AI_CHAT_STREAM_PATH,
    credentials: 'include',
    // Unstripped, like httpHelper's X-Location: it reports the URL the user is actually on
    headers: () => ({ 'X-Location': globalThis.location.pathname }),
    prepareSendMessagesRequest: ({ messages, body }) => ({
      body: toChatRequestBody(messages, body?.conversationId, context, body?.answerLanguage)
    })
  })
